import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import ReferenceInfoDialog from '../components/ReferenceInfoDialog';
import MrpRecommendationImportDialog from '../components/MrpRecommendationImportDialog';
import { departmentSelectOptions, coerceLegacyDepartmentToFy } from '../utils/poDepartmentOptions';

/* ─────────────── ThreeDotsPicker ─────────────── */
const ThreeDotsPicker = ({ onClick, title }) => (
  <button type="button" onClick={onClick} title={title}
    className="absolute right-1.5 top-1/2 -translate-y-1/2 px-1 text-sap-textLight hover:text-sap-blue transition-colors">
    <span className="text-base leading-none">⋯</span>
  </button>
);

/* ─────────────── ProjectPickerDialog ─────────────── */
const ProjectPickerDialog = ({ current, onSelect, onClose }) => {
  const [search, setSearch] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState('');
  const [selected, setSelected] = React.useState(null);
  const [showNew, setShowNew] = React.useState(false);
  const [newCode, setNewCode] = React.useState('');
  const [newName, setNewName] = React.useState('');
  const [creating, setCreating] = React.useState(false);
  const [createErr, setCreateErr] = React.useState('');

  const reload = React.useCallback(async (q) => {
    try {
      setLoading(true);
      const r = await api.get(`/projects?search=${encodeURIComponent(q || '')}`);
      setRows(r || []);
    } catch (e) { setError(e.message || 'Failed to load projects'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(() => { if (!cancelled) reload(search); }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [search, reload]);

  const handleChoose = () => { if (selected) onSelect(selected); };
  const handleNew = async () => {
    if (!newCode.trim()) { setCreateErr('Project code is required'); return; }
    setCreating(true); setCreateErr('');
    try {
      const created = await api.post('/projects', { project_code: newCode.trim(), project_name: newName.trim() });
      setShowNew(false); setNewCode(''); setNewName('');
      await reload(search);
      onSelect(created);
    } catch (e) { setCreateErr(e.message || 'Failed to create project'); }
    finally { setCreating(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[720px] max-h-[560px] flex flex-col" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">List of Projects</span>
          <button onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)} placeholder="Find project code or name…"
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? <div className="py-10 text-center text-xs text-sap-textLight">Loading…</div>
          : error ? <div className="py-10 text-center text-xs text-red-600">{error}</div>
          : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
                <tr>
                  <th className="text-left px-3 py-2 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-40">Project Code</th>
                  <th className="text-left px-3 py-2 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">Project Name</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((p, i) => {
                  const code =
                    String(
                      p?.project_code
                      ?? p?.project_no
                      ?? p?.code
                      ?? p?.PROJECT_CODE
                      ?? ''
                    ).trim();
                  const name =
                    String(
                      p?.project_name
                      ?? p?.name
                      ?? p?.PROJECT_NAME
                      ?? ''
                    ).trim();
                  const isSel = selected && String(
                    selected.project_code ?? selected.project_no ?? selected.code ?? ''
                  ).trim() === code;
                  const isCur = code !== '' && code === String(current || '').trim();
                  return (
                    <tr key={code || `row-${i}`} onClick={() => setSelected({ project_code: code, project_name: name })} onDoubleClick={() => onSelect({ project_code: code, project_name: name })}
                      className={`border-t border-sap-border/20 cursor-pointer transition-colors ${isSel ? 'bg-sap-blue/15 ring-1 ring-sap-blue/30' : isCur ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'}`}>
                      <td className="px-3 py-2 font-mono font-semibold text-sap-blue">{code || '—'}</td>
                      <td className="px-3 py-2 text-sap-text">{name || '—'}</td>
                    </tr>
                  );
                })}
                {rows.length === 0 && <tr><td colSpan={2} className="px-3 py-6 text-center text-sap-textLight">No projects found</td></tr>}
              </tbody>
            </table>
          )}
        </div>
        {showNew && (
          <div className="px-4 py-3 border-t border-sap-border bg-blue-50/50 space-y-2">
            <div className="text-xs font-semibold text-sap-text">New Project</div>
            <div className="flex gap-2">
              <input value={newCode} onChange={e => setNewCode(e.target.value)} placeholder="Project Code *"
                className="flex-1 h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
              <input value={newName} onChange={e => setNewName(e.target.value)} placeholder="Project Name"
                className="flex-[2] h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
            </div>
            {createErr && <div className="text-2xs text-red-600">{createErr}</div>}
            <div className="flex gap-2">
              <button onClick={handleNew} disabled={creating} className="px-3 py-1 text-xs font-semibold bg-sap-blue text-white rounded hover:bg-sap-blueDark disabled:opacity-50">
                {creating ? 'Creating…' : 'Create'}</button>
              <button onClick={() => { setShowNew(false); setCreateErr(''); }} className="px-3 py-1 text-xs text-sap-textLight border border-sap-border rounded hover:bg-gray-100">Cancel</button>
            </div>
          </div>
        )}
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl flex items-center justify-between">
          <span className="text-2xs text-sap-textLight">{rows.length} project{rows.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button onClick={handleChoose} disabled={!selected} className="btn-primary-sm disabled:opacity-40">Choose</button>
            <button onClick={onClose} className="px-4 py-1.5 text-xs font-semibold border border-sap-border rounded-lg hover:bg-gray-100 transition-all">Cancel</button>
            <button onClick={() => setShowNew(v => !v)} className="px-4 py-1.5 text-xs font-semibold border border-sap-blue text-sap-blue rounded-lg hover:bg-sap-blue/5 transition-all">New</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── ProjectField ─────────────── */
function ProjectField({ value, onChange, disabled }) {
  const [open, setOpen] = React.useState(false);
  return (
    <>
      <div className="relative">
        <input value={value || ''} onChange={e => onChange(e.target.value)} disabled={disabled}
          className={`w-full h-8 px-3 pr-7 text-xs border border-sap-border rounded-lg focus:outline-none focus:border-sap-blue ${disabled ? 'bg-gray-100 text-gray-500' : 'bg-white'}`} />
        {!disabled && <ThreeDotsPicker title="Choose Project" onClick={() => setOpen(true)} />}
      </div>
      {open && (
        <ProjectPickerDialog current={value}
          onSelect={(p) => { onChange(p.project_code || ''); setOpen(false); }}
          onClose={() => setOpen(false)} />
      )}
    </>
  );
}

/* ─────────────── YellowArrow (SAP B1 style) ─────────────── */
const YellowArrow = ({ onClick, title, disabled = false }) => (
  <button
    type="button"
    onClick={disabled ? undefined : onClick}
    title={title}
    disabled={disabled}
    className={`flex-shrink-0 flex items-center justify-center w-5 h-5 transition-opacity ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-80'}`}
  >
    <svg width="12" height="12" viewBox="0 0 14 14" className="drop-shadow-sm">
      <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
    </svg>
  </button>
);

/* ═══════════════════ SapReferencePickerDialog (Tax / Distribution) ═══════════════════ */
const SapReferencePickerDialog = ({ type, current, onSelect, onClose }) => {
  const isTax = type === 'tax';
  const title = isTax ? 'Tax Codes' : 'Distribution Rules';
  const [search, setSearch] = React.useState('');
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [selected, setSelected] = React.useState(current || '');

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const path = isTax ? '/sap-reference/tax-codes' : '/sap-reference/distribution-rules';
        const data = await api.get(`${path}?search=${encodeURIComponent(search)}&limit=200`);
        if (!cancelled) setRows(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [search, isTax]);

  const codeKey = isTax ? 'tax_code' : 'rule_code';
  const nameKey = isTax ? 'tax_name' : 'description';

  return (
    <div className="fixed inset-0 z-[100070] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[640px] max-h-[520px] flex flex-col"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-white text-xs font-semibold">{title}</span>
          <button type="button" onClick={onClose} className="text-white hover:opacity-80 text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={isTax ? 'Search code or name…' : 'Search rule or description…'}
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
          />
        </div>
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
              <tr>
                <th className="text-left px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-28">Code</th>
                <th className="text-left px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">Name / Description</th>
                {isTax && (
                  <th className="text-right px-3 py-1.5 text-2xs font-semibold text-sap-textLight uppercase tracking-wider w-16">Rate %</th>
                )}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={isTax ? 3 : 2} className="px-3 py-4 text-center text-sap-textLight">Loading…</td></tr>
              )}
              {!loading && rows.map((r) => {
                const code = r[codeKey];
                const isCurrent = String(code) === String(selected);
                return (
                  <tr
                    key={code}
                    onClick={() => setSelected(code)}
                    onDoubleClick={() => onSelect(r)}
                    className={`border-t border-sap-border/20 cursor-pointer transition-colors
                      ${isCurrent ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'}`}
                  >
                    <td className="px-3 py-1.5 text-sap-blue font-mono font-semibold">{code}</td>
                    <td className="px-3 py-1.5 text-sap-text">{r[nameKey] || '—'}</td>
                    {isTax && (
                      <td className="px-3 py-1.5 text-right text-sap-textLight">{Number(r.rate_pct).toFixed(2)}</td>
                    )}
                  </tr>
                );
              })}
              {!loading && rows.length === 0 && (
                <tr><td colSpan={isTax ? 3 : 2} className="px-3 py-4 text-center text-sap-textLight">No records — import master data Excel files</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl flex justify-between items-center">
          <span className="text-2xs text-sap-textLight">Double-click or Choose • {rows.length} row{rows.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button type="button" onClick={onClose}
              className="px-3 py-1 text-xs border border-sap-border rounded-lg hover:bg-gray-100">Cancel</button>
            <button type="button" disabled={!selected}
              onClick={() => {
                const row = rows.find((r) => String(r[codeKey]) === String(selected));
                if (row) onSelect(row);
              }}
              className="px-4 py-2 text-xs font-semibold bg-sap-blue text-white rounded-lg disabled:opacity-40 hover:bg-sap-blueDark">Choose</button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* ── constants ── */
const STATUS_OPTS  = ['Planned', 'Released', 'In Progress', 'Completed', 'Closed', 'Cancelled'];
const TYPE_OPTS    = ['Standard', 'Special', 'Disassembly'];
const ORIGIN_OPTS  = ['Manual', 'Sales Order', 'MRP'];
const UOM_OPTS     = ['PCS', 'KG', 'MTR', 'LTR', 'SET', 'NOS', 'Each'];
const ISSUE_OPTS   = ['Backflush', 'Manual'];
const ROUTING_OPTS = ['On Start Date', 'On Due Date', 'Backward', 'Forward'];
const LINKED_TO_OPTS = ['', 'Sales Order', 'Purchase Order'];

const statusColor = (s) => ({
  'Planned':     'bg-[#FEF5E7] text-[#B8860B]',
  'Released':    'bg-[#EDF2F7] text-[#4A6FA5]',
  'In Progress': 'bg-[#F1EEF6] text-[#7B6FA0]',
  'Completed':   'bg-[#EDF5F0] text-[#4A9E6E]',
  'Closed':      'bg-[#EDF5F0] text-[#4A9E6E]',
  'Cancelled':   'bg-[#F0F4F3] text-[#6B7F7F]',
}[s] || 'bg-[#F0F4F3] text-[#6B7F7F]');

const blankOrder = {
  order_no: '', series_id: '', series: '', item_id: '', item_no: '', description: '',
  planned_qty: '', due_date: '', start_date: '', status: 'Planned',
  prod_type: 'Standard', warehouse_id: '', linked_so_id: '',
  priority: 100, project_no: '', remarks: '', origin: 'Manual',
  order_date: new Date().toISOString().slice(0, 10),
  uom_name: 'PCS', customer: '', distr_rule: '',
  routing_date_calc: 'On Start Date', pick_pack_remarks: '',
  journal_remark: '', completed_qty: 0, rejected_qty: 0,
  actual_closing_date: '',
  total_material_cost: 0, total_resource_cost: 0, total_cost: 0,
  linked_to: 'Sales Order', user: '', procure_items: false,
  department: '',
  subcontractor_vendor_id: '',
};

/* ── Shared primitives ── */
const inp = 'w-full h-8 px-3 text-xs border border-sap-border rounded-lg focus:outline-none focus:border-sap-blue focus:ring-1 focus:ring-sap-blue/20';
const sel = `${inp} bg-white`;
const dis = 'bg-gray-100 text-gray-500 cursor-not-allowed';
const lbl = 'w-36 text-xs text-sap-textLight text-right shrink-0';
const thCls = 'text-left px-3 py-2 font-semibold text-sap-textMuted uppercase tracking-wider text-2xs whitespace-nowrap';

function Field({ label, value, onChange, type = 'text', disabled, children }) {
  return (
    <div className="flex items-center gap-2">
      <label className={lbl}>{label}</label>
      {children || (
        <input type={type} value={value ?? ''} disabled={disabled}
          onChange={e => onChange?.(e.target.value)}
          className={`flex-1 ${inp} ${disabled ? dis : 'bg-white'}`} />
      )}
    </div>
  );
}

function Banner({ type, children }) {
  const cls = {
    error:   'bg-red-50 border-red-200 text-red-700',
    success: 'bg-green-50 border-green-200 text-green-700',
    info:    'bg-blue-50 border-blue-200 text-blue-700',
  }[type] || 'bg-gray-50 border-gray-200 text-gray-700';
  return <div className={`px-3 py-1.5 text-xs border rounded-lg ${cls}`}>{children}</div>;
}

function SummaryRow({ label, value, valueClass = '' }) {
  return (
    <div className="flex items-center gap-2">
      <label className="w-52 text-xs text-sap-textLight text-right shrink-0">{label}</label>
      <span className={`flex-1 h-8 flex items-center px-3 text-xs border border-sap-border rounded-lg bg-gray-100 text-sap-text ${valueClass}`}>
        {value}
      </span>
    </div>
  );
}

/* ─────────────────────────────── General Modal Dialog (SAP B1 style) ────────────────── */
const GeneralModalDialog = ({ form, components, subcontractorLabel, onClose }) => {
  const priorityText = React.useMemo(() => {
    const p = Number(form.priority) || 100;
    if (p < 50) return 'Low';
    if (p > 100) return 'High';
    return 'Medium';
  }, [form.priority]);

  const compBreakdown = React.useMemo(() => {
    return (components || []).map((c, i) => {
      const pq = Number(c.planned_qty) || 0;
      const uc = Number(c.unit_cost) || 0;
      const tc = Number(c.total_cost) || (pq * uc);
      return {
        line: i + 1,
        no: c.item_no || c.resource_no || c.comp_id || '—',
        description: c.description || '—',
        line_type: c.line_type || 'Item',
        planned_qty: pq,
        unit_cost: uc,
        total_cost: tc,
        uom: c.uom || 'NOS'
      };
    });
  }, [components]);

  const totalBudgetAmount = React.useMemo(() => {
    const sum = compBreakdown.reduce((acc, c) => acc + c.total_cost, 0);
    return sum || Number(form.total_cost) || 0;
  }, [compBreakdown, form.total_cost]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[540px] max-h-[90vh] flex flex-col overflow-hidden animate-in fade-in zoom-in-95 duration-150" onClick={e => e.stopPropagation()}>
        {/* SAP B1 Style Title Bar */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-[#E8E4DC] border-b border-[#C4BFB6]">
          <div className="flex items-center gap-2 text-xs font-semibold text-[#333]">
            <span className="text-[#666] font-mono text-[11px] cursor-default">|◀ |▶|</span>
            <span className="flex items-center gap-1 font-bold text-sm text-[#222]">
              General
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="6 9 12 15 18 9"/></svg>
            </span>
          </div>
          <button onClick={onClose} className="text-[#666] hover:text-[#000] font-bold text-base leading-none">✕</button>
        </div>

        {/* Modal Content */}
        <div className="p-5 overflow-y-auto space-y-4 text-xs">
          {/* Main Form Fields — matching SAP B1 screenshot */}
          <div className="bg-[#FAF9F5] border border-[#E0DCD3] rounded-xl p-4 space-y-3 shadow-inner">
            <div className="flex items-center justify-between">
              <label className="text-sap-textLight font-medium w-44">Document Number</label>
              <input type="text" readOnly value={form.order_no || '—'} className="flex-1 h-7 px-2.5 border border-[#C4BFB6] rounded bg-white text-sap-text font-mono text-xs" />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sap-textLight font-medium w-44">Unit</label>
              <input type="text" readOnly value={form.uom_name || 'NOS'} className="flex-1 h-7 px-2.5 border border-[#C4BFB6] rounded bg-white text-sap-text text-xs" />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sap-text font-bold w-44 text-sap-blue">Budget Amount</label>
              <input type="text" readOnly value={totalBudgetAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} className="flex-1 h-7 px-2.5 border border-sap-blue rounded bg-sap-blueLight/30 text-sap-blue font-bold text-xs shadow-sm" />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sap-textLight font-medium w-44">Original Production Order</label>
              <input type="text" readOnly value={form.origin || form.linked_so_number || '—'} className="flex-1 h-7 px-2.5 border border-[#C4BFB6] rounded bg-white text-sap-text text-xs" />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sap-textLight font-medium w-44">Priority</label>
              <input type="text" readOnly value={priorityText} className="flex-1 h-7 px-2.5 border border-[#C4BFB6] rounded bg-white text-sap-text text-xs" />
            </div>

            <div className="flex items-center justify-between">
              <label className="text-sap-textLight font-medium w-44">Supplier</label>
              <input type="text" readOnly value={subcontractorLabel || form.customer || '—'} className="flex-1 h-7 px-2.5 border border-[#C4BFB6] rounded bg-white text-sap-text text-xs" />
            </div>
          </div>

          {/* Detailed Calculation Breakdown */}
          <div className="space-y-2">
            <div className="flex items-center justify-between border-b pb-1.5 border-sap-border/60">
              <h4 className="font-bold text-sap-text text-xs">Budget Amount Calculation</h4>
              <span className="text-[11px] text-sap-blue font-mono font-semibold">
                Total Budget: ₹ {totalBudgetAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>

            <p className="text-[11px] text-sap-textLight italic">
              Budget Amount is calculated as the sum of <span className="font-semibold text-sap-text">Planned Quantity × Unit Cost</span> for all component lines.
            </p>

            {compBreakdown.length === 0 ? (
              <div className="p-4 bg-gray-50 border border-dashed border-gray-300 rounded-lg text-center text-gray-500 italic text-xs">
                No component lines added yet. Add components to view itemized budget cost calculations.
              </div>
            ) : (
              <div className="border border-sap-border rounded-lg overflow-hidden max-h-48 overflow-y-auto shadow-sm">
                <table className="w-full text-xs">
                  <thead className="bg-[#E8E4DC] sticky top-0 border-b border-[#C4BFB6]">
                    <tr>
                      <th className="px-2 py-1.5 text-left font-semibold text-[#333]">#</th>
                      <th className="px-2 py-1.5 text-left font-semibold text-[#333]">Component / Resource</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-[#333]">Qty</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-[#333]">Unit Cost</th>
                      <th className="px-2 py-1.5 text-right font-semibold text-[#333]">Budget Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {compBreakdown.map(c => (
                      <tr key={c.line} className="border-b border-sap-border/30 hover:bg-sap-blueLight/10">
                        <td className="px-2 py-1 text-gray-500 text-center">{c.line}</td>
                        <td className="px-2 py-1 max-w-[160px] truncate" title={c.description}>
                          <span className="font-semibold text-sap-text block truncate">{c.no}</span>
                          <span className="text-gray-400 block text-[10px] truncate">{c.description}</span>
                        </td>
                        <td className="px-2 py-1 text-right font-mono">{c.planned_qty} {c.uom}</td>
                        <td className="px-2 py-1 text-right font-mono">₹ {c.unit_cost.toFixed(2)}</td>
                        <td className="px-2 py-1 text-right font-mono font-semibold text-sap-blue">₹ {c.total_cost.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot className="bg-[#F4F3EF] border-t border-[#C4BFB6] font-bold">
                    <tr>
                      <td colSpan={4} className="px-2.5 py-2 text-right text-sap-text">Total Budget Amount:</td>
                      <td className="px-2.5 py-2 text-right font-mono text-sap-blue text-xs">₹ {totalBudgetAmount.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 bg-[#F4F3EF] border-t border-[#C4BFB6] flex justify-end">
          <button onClick={onClose} className="px-5 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded hover:bg-sap-blueDark shadow-sm">
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════ */
export default function ProductionOrderDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [form, setForm]               = useState({ ...blankOrder });
  const [components, setComponents]   = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading]         = useState(!isNew);
  const [saving, setSaving]           = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const [tab, setTab]                 = useState('components');
  const [warehouses, setWarehouses]   = useState([]);
  const [users, setUsers]             = useState([]);
  const [series, setSeries]           = useState([]);
  const [showRefInfo, setShowRefInfo] = useState(false);
  const [refsTo, setRefsTo]           = useState([]);
  const [refsBy, setRefsBy]           = useState([]);
  const [showRelMap, setShowRelMap]     = useState(false);
  const [relMapIssues, setRelMapIssues] = useState([]);
  const [relMapReceipts, setRelMapReceipts] = useState([]);
  const [relMapLoading, setRelMapLoading] = useState(false);
  const [distPickerOpen, setDistPickerOpen] = useState(false);
  const [showGeneralModal, setShowGeneralModal] = useState(false);

  const departmentOpts = useMemo(
    () => departmentSelectOptions(form.department),
    [form.department]
  );

  const openRelMap = async () => {
    setShowRelMap(true);
    if (isNew) return;
    setRelMapLoading(true);
    try {
      const data = await api.get(`/production-orders/${id}`);
      setRelMapIssues(Array.isArray(data?.issues) ? data.issues : []);
      setRelMapReceipts(Array.isArray(data?.receipts) ? data.receipts : []);
    } catch { /* keep whatever was loaded at page load */ }
    finally { setRelMapLoading(false); }
  };

  /* item search */
  const [itemSearch, setItemSearch]    = useState('');
  const [items, setItems]              = useState([]);
  const [showItemDrop, setShowItemDrop] = useState(false);

  /* subcontractor vendor (searchable combobox) */
  const [subcontractorLabel, setSubcontractorLabel] = useState('');
  const [subcontractorSearch, setSubcontractorSearch] = useState('');
  const [subcontractorResults, setSubcontractorResults] = useState([]);
  const [showSubcontractorDD, setShowSubcontractorDD] = useState(false);
  const [subcontractorListLoading, setSubcontractorListLoading] = useState(false);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const pickSubcontractor = (v) => {
    set('subcontractor_vendor_id', String(v.vendor_id));
    setSubcontractorLabel(`${v.vendor_code} — ${v.vendor_name || ''}`.replace(/\s*—\s*$/, '').trim());
    setSubcontractorSearch('');
    setShowSubcontractorDD(false);
  };

  const clearSubcontractor = () => {
    set('subcontractor_vendor_id', '');
    setSubcontractorLabel('');
    setSubcontractorSearch('');
  };

  /** SAP-style: BP Project = SO bp_project || document No.; fill Project field and Distribution Rule when linking SO if empty. */
  useEffect(() => {
    const soId = parseInt(String(form.linked_so_id || '').trim(), 10);
    if (!soId) return;

    const projectEmpty = !String(form.project_no || '').trim();
    const distrEmpty = !String(form.distr_rule || '').trim();
    if (!projectEmpty && !distrEmpty) return;

    let cancelled = false;
    (async () => {
      try {
        const so = await api.get(`/sales-orders/${soId}`);
        if (cancelled || !so) return;

        setForm(f => {
          const patch = {};
          if (!String(f.project_no || '').trim()) {
            const bp = String(so.bp_project || '').trim();
            const sn = String(so.so_number || '').trim();
            const code = bp || sn;
            if (code) patch.project_no = code;
          }
          if (!String(f.distr_rule || '').trim() && so.distr_rule) {
            patch.distr_rule = so.distr_rule;
          }
          return Object.keys(patch).length > 0 ? { ...f, ...patch } : f;
        });
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [form.linked_so_id, form.project_no, form.distr_rule]);

  /* load warehouses + users */
  useEffect(() => {
    api.get('/production-orders/warehouses')
      .then(d => setWarehouses(Array.isArray(d) ? d : []))
      .catch(() => {});
    api.get('/production-orders/users')
      .then(d => setUsers(Array.isArray(d) ? d : []))
      .catch(() => {});
    api.get('/production-orders/series')
      .then(d => {
        const seriesList = Array.isArray(d) ? d : [];
        setSeries(seriesList);
        if (isNew && seriesList.length > 0) {
          const def = seriesList.find(s => s.is_default) || seriesList[0];
          setForm(f => ({ ...f, series_id: def.series_id }));
          api.get(`/production-orders/next-number?series_id=${def.series_id}`)
            .then(numData => setForm(f => ({ ...f, order_no: numData.next })))
            .catch(() => {});
        }
      })
      .catch(() => setSeries([]));
  }, []);

  /* handle series change */
  const handleSeriesChange = (seriesId) => {
    const sid = parseInt(seriesId, 10);
    const chosen = series.find(s => s.series_id === sid);
    if (!chosen) return;
    setForm(f => ({ ...f, series_id: sid }));
    if (isNew) {
      api.get(`/production-orders/next-number?series_id=${sid}`)
        .then(d => setForm(f => ({ ...f, order_no: d.next })))
        .catch(() => {});
    }
  };

  /* subcontractor vendor search (when picker open) */
  useEffect(() => {
    const locked = form.status === 'Completed' || form.status === 'Closed' || form.status === 'Cancelled';
    if (!showSubcontractorDD || locked) return;
    const t = setTimeout(() => {
      setSubcontractorListLoading(true);
      api.get(`/vendors?search=${encodeURIComponent(subcontractorSearch.trim())}&limit=50`)
        .then((r) => setSubcontractorResults(Array.isArray(r) ? r : (r.rows || [])))
        .catch(() => setSubcontractorResults([]))
        .finally(() => setSubcontractorListLoading(false));
    }, 200);
    return () => clearTimeout(t);
  }, [subcontractorSearch, showSubcontractorDD, form.status]);

  /* load existing order */
  useEffect(() => {
    if (isNew) {
      clearSubcontractor();
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const d = await api.get(`/production-orders/${id}`);
        setForm({
          order_no:    d.order_no || '',
          series:      d.series || '',
          series_id:   d.series_id || '',
          item_id:     d.item_id || '',
          item_no:     d.item_no || '',
          description: d.description || '',
          planned_qty: d.planned_qty ?? '',
          due_date:    d.due_date ? d.due_date.slice(0, 10) : '',
          start_date:  d.start_date ? d.start_date.slice(0, 10) : '',
          order_date:  d.order_date ? d.order_date.slice(0, 10) : (d.created_at ? d.created_at.slice(0, 10) : ''),
          status:      d.status || 'Planned',
          prod_type:   d.prod_type || 'Standard',
          warehouse_id: d.warehouse_id || '',
          linked_so_id: d.linked_so_id || '',
          priority:    d.priority || 100,
          project_no:  d.project_no || '',
          remarks:     d.remarks || '',
          origin:      d.origin || 'Manual',
          uom_name:    d.uom_name || 'PCS',
          customer:    d.customer || '',
          distr_rule:  d.distr_rule || '',
          routing_date_calc: d.routing_date_calc || 'On Start Date',
          pick_pack_remarks: d.pick_pack_remarks || '',
          journal_remark:    d.journal_remark || '',
          completed_qty:     d.completed_qty || 0,
          rejected_qty:      d.rejected_qty || 0,
          actual_closing_date: d.actual_closing_date ? d.actual_closing_date.slice(0, 10) : '',
          total_material_cost: d.total_material_cost || 0,
          total_resource_cost: d.total_resource_cost || 0,
          total_cost:  d.total_cost || 0,
          linked_so_number: d.linked_so_number || '',
          wh_code: d.wh_code || '',
          wh_name: d.wh_name || '',
          linked_to: d.linked_to || 'Sales Order',
          user: d.user || '',
          procure_items: d.procure_items || false,
          department: coerceLegacyDepartmentToFy(d.department ? String(d.department) : ''),
          subcontractor_vendor_id: d.subcontractor_vendor_id != null ? String(d.subcontractor_vendor_id) : '',
        });
        const sc = d.subcontractor_vendor_code ? String(d.subcontractor_vendor_code).trim() : '';
        const svn = d.subcontractor_vendor_name ? String(d.subcontractor_vendor_name).trim() : '';
        setSubcontractorLabel(
          d.subcontractor_vendor_id
            ? (sc && svn ? `${sc} — ${svn}` : sc || svn || `Vendor #${d.subcontractor_vendor_id}`)
            : ''
        );
        setItemSearch(d.item_no ? `${d.item_no} — ${d.description}` : '');
        // If no components saved in DB, auto-populate the product item itself as the first component
        const loadedComps = d.components || [];
        if (loadedComps.length === 0 && d.item_id && d.item_no) {
          const uCost = Number(d.resolved_price) || 0;
          const planQty = Number(d.planned_qty) || 1;
          loadedComps.push({
            comp_id: `auto-product-${Date.now()}`,
            line_type: 'Item',
            item_id: d.item_id,
            item_no: d.item_no,
            resource_id: null,
            resource_no: null,
            description: d.description || '',
            base_qty: 1,
            base_ratio: 1,
            planned_qty: planQty,
            uom: d.uom_name || 'PCS',
            warehouse_id: d.warehouse_id || '',
            issue_method: 'Backflush',
            unit_cost: uCost,
            total_cost: planQty * uCost,
            issued_qty: 0,
            comp_wh_code: d.wh_code || '',
          });
        }
        setComponents(loadedComps);
        setRelMapIssues(d.issues || []);
        setRelMapReceipts(d.receipts || []);
        try {
          const att = await api.get(`/production-orders/${id}/attachments`);
          setAttachments(Array.isArray(att) ? att : []);
        } catch { setAttachments([]); }
      } catch {
        setError('Failed to load production order');
      } finally {
        setLoading(false);
      }
    })();
  }, [id, isNew]);

  /* item search loader */
  const searchItems = useCallback(async (q) => {
    if (!q || q.length < 1) { setItems([]); return; }
    try {
      const d = await api.get(`/items?limit=50&search=${encodeURIComponent(q)}`);
      setItems(d.rows || []);
    } catch { setItems([]); }
  }, []);

  const pickItem = async (it) => {
    setForm(f => ({ ...f, item_id: it.item_id, item_no: it.item_no, description: it.description }));
    setItemSearch(`${it.item_no} — ${it.description}`);
    setShowItemDrop(false);

    try {
      const data = await api.get(`/production-orders/item-bom/${it.item_id}`);
      const item = data.item || {};
      const bom = data.bom;
      const bomComponents = data.components || [];

      const compWh = bomComponents.length > 0 ? bomComponents[0].warehouse_id : null;
      setForm(f => ({
        ...f,
        uom_name: item.uom_name || f.uom_name || 'PCS',
        planned_qty: bom ? Number(bom.quantity) || 1 : f.planned_qty,
        warehouse_id: bom?.warehouse_id || compWh || item.default_warehouse_id || f.warehouse_id || '',
        project_no: bom?.project || f.project_no || '',
      }));

      setForm((f) => ({ ...f, prod_type: bomComponents.length > 0 ? 'Standard' : 'Special' }));

      if (bomComponents.length > 0) {
        const baseQty = bom ? Number(bom.quantity) || 1 : 1;
        const plannedQty = Number(form.planned_qty) || baseQty;
        setComponents(bomComponents.map((c, idx) => ({
          comp_id: `bom-${idx}`,
          line_type: 'Item',
          item_id: c.item_id,
          item_no: c.item_no,
          resource_id: null,
          resource_no: null,
          description: c.description || '',
          base_qty: Number(c.base_qty) || 0,
          base_ratio: baseQty,
          planned_qty: baseQty ? (plannedQty * (Number(c.base_qty) || 0)) / baseQty : 0,
          uom: c.uom || 'NOS',
          warehouse_id: c.warehouse_id || bom?.warehouse_id || '',
          issue_method: c.issue_method || 'Manual',
          unit_cost: Number(c.unit_cost) || 0,
          total_cost: (baseQty ? (plannedQty * (Number(c.base_qty) || 0)) / baseQty : 0) * (Number(c.unit_cost) || 0),
          issued_qty: 0,
        })));
      } else {
        // No bill of material, so no components. This used to add the product as a
        // component of itself, which is how a Special order arrived asking for one of
        // the thing it was about to make. A Special order starts empty and the planner
        // adds the items and resources the job needs.
        setComponents([]);
      }
    } catch {
      // item-bom lookup failed; header fields from search result are enough
    }
  };

  /* save */
  const handleSave = async () => {
    setError(''); setSuccess('');
    if (!form.item_no)     return setError('Product is required');
    if (!form.planned_qty) return setError('Planned quantity is required');
    if (!form.due_date)    return setError('Due date is required');

    setSaving(true);
    try {
      const payload = {
        order_no:    form.order_no,
        series_id:   form.series_id || null,
        item_id:     form.item_id || null,
        item_no:     form.item_no,
        description: form.description,
        planned_qty: Number(form.planned_qty),
        due_date:    form.due_date,
        start_date:  form.start_date || null,
        status:      form.status,
        prod_type:   form.prod_type || 'Standard',
        warehouse_id: form.warehouse_id || null,
        linked_so_id: form.linked_so_id || null,
        priority:    Number(form.priority) || 100,
        project_no:  form.project_no || null,
        remarks:     form.remarks || null,
        origin:      form.origin || 'Manual',
        uom_name:    form.uom_name || 'PCS',
        customer:    form.customer || null,
        distr_rule:  form.distr_rule || null,
        routing_date_calc: form.routing_date_calc || 'On Start Date',
        pick_pack_remarks: form.pick_pack_remarks || null,
        journal_remark:    form.journal_remark || null,
        completed_qty:     Number(form.completed_qty) || 0,
        rejected_qty:      Number(form.rejected_qty) || 0,
        actual_closing_date: form.actual_closing_date || null,
        subcontractor_vendor_id: form.subcontractor_vendor_id
          ? Number(form.subcontractor_vendor_id)
          : null,
        department: form.department || null,
      };

      if (isNew) {
        if (form.series_id) {
          const consumed = await api.post('/production-orders/consume-number', { series_id: form.series_id });
          if (consumed?.consumed) {
            payload.order_no = consumed.consumed;
            payload.series = consumed.series_name;
          }
        }
        if (components.length > 0) {
          payload.components = components.map(c => ({
            line_type: c.line_type || 'Item',
            item_id: c.item_id || null,
            resource_id: c.resource_id || null,
            description: c.description || '',
            base_qty: Number(c.base_qty) || 0,
            base_ratio: Number(c.base_ratio) || 1,
            planned_qty: Number(c.planned_qty) || 0,
            uom: c.uom || 'NOS',
            warehouse_id: c.warehouse_id || null,
            issue_method: c.issue_method || 'Manual',
            unit_cost: Number(c.unit_cost) || 0,
            total_cost: Number(c.total_cost) || 0,
          }));
        }
        const d = await api.post('/production-orders', payload);
        setSuccess('Production order created');
        setTimeout(() => navigate(`/production-order/${d.prod_order_id}`), 600);
      } else {
        const putData = await api.put(`/production-orders/${id}`, payload);
        if (putData != null && typeof putData === 'object' && 'department' in putData) {
          const d = putData.department;
          set(
            'department',
            d != null && String(d).trim() !== '' ? coerceLegacyDepartmentToFy(String(d)) : ''
          );
        } else {
          try {
            const snap = await api.get(`/production-orders/${id}`);
            const d = snap?.department;
            set(
              'department',
              d != null && String(d).trim() !== '' ? coerceLegacyDepartmentToFy(String(d)) : ''
            );
          } catch {
            /* leave form as-is if refresh fails */
          }
        }
        setSuccess('Production order updated');
      }
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteOrder = async () => {
    if (!window.confirm('Are you sure you want to permanently delete this Production Order? This will also delete all its components and attachments, freeing up the order number for reuse.')) return;
    
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await api.delete(`/production-orders/${id}`);
      setSuccess('Production order permanently deleted');
      setTimeout(() => navigate('/production/orders'), 1000);
    } catch (e) {
      setError(e.message || 'Failed to delete production order');
      setSaving(false);
    }
  };

  const isLocked = form.status === 'Completed' || form.status === 'Closed' || form.status === 'Cancelled';
  const isOverdue = form.due_date && new Date(form.due_date) < new Date() && form.status !== 'Completed' && form.status !== 'Closed' && form.status !== 'Cancelled';

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin h-6 w-6 border-2 border-sap-blue border-t-transparent rounded-full" />
    </div>
  );

  const filteredItems = items.filter(i =>
    !itemSearch || i.item_no?.toLowerCase().includes(itemSearch.toLowerCase()) ||
    i.description?.toLowerCase().includes(itemSearch.toLowerCase())
  );

  return (
    <div className="space-y-4">
      {/* ── Header bar ── */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/production/orders')}
            className="h-8 w-8 flex items-center justify-center rounded-lg border border-sap-border
                       hover:bg-sap-blueLight transition-colors text-sap-textLight">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <polyline points="15 18 9 12 15 6" />
            </svg>
          </button>
          <div>
            <h1 className="text-base font-bold text-sap-text">
              Production Order {!isNew && `- ${form.order_no}`}
            </h1>
            {!isNew && (
              <span className={`inline-block mt-0.5 px-2 py-0.5 rounded-full text-2xs font-semibold ${statusColor(form.status)}`}>
                {form.status}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowGeneralModal(true)}
            className="px-4 py-2 text-xs font-semibold bg-[#E8A000]/10 text-[#B3853F] border border-[#E8A000]/40 rounded-lg hover:bg-[#E8A000]/20 transition-colors flex items-center gap-1.5 shadow-sm"
            title="Open General SAP B1 Production Order Details & Calculation">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="4" y1="9" x2="20" y2="9"/><line x1="4" y1="15" x2="20" y2="15"/><line x1="10" y1="3" x2="10" y2="21"/>
            </svg>
            General
          </button>
          {!isNew && (
            <button onClick={openRelMap}
              className="px-4 py-2 text-xs font-medium border border-sap-border rounded-lg hover:bg-sap-blueLight transition-colors flex items-center gap-1.5">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="2" y="7" width="7" height="10" rx="1"/><rect x="15" y="7" width="7" height="10" rx="1"/>
                <line x1="9" y1="12" x2="15" y2="12"/><polyline points="13,10 15,12 13,14"/>
              </svg>
              Relationship Map
            </button>
          )}
          {!isNew && (
            <button onClick={handleDeleteOrder} disabled={saving}
              className="px-4 py-2 text-xs font-semibold text-red-600 border border-red-200 rounded-lg hover:bg-red-50 disabled:opacity-40 shadow-sm transition-all">
              Delete
            </button>
          )}
          <button onClick={() => navigate('/production/orders')}
            className="px-4 py-2 text-xs font-medium border border-sap-border rounded-lg hover:bg-white transition-colors">
            Cancel
          </button>
          <button onClick={handleSave} disabled={saving || isLocked}
            className="px-5 py-2 text-xs font-semibold bg-sap-blue text-white rounded-lg
                       hover:bg-sap-blueDark disabled:opacity-40 shadow-sm transition-all">
            {saving ? 'Saving…' : isNew ? 'Add' : 'Update'}
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && <Banner type="error">{error}</Banner>}
      {success && <Banner type="success">{success}</Banner>}
      {isLocked && <Banner type="info">This order is {form.status.toLowerCase()} and cannot be edited.</Banner>}

      {/* ═══════════════════════════════════════════════════════
          MAIN FORM — SAP B1 two-column layout
          ═══════════════════════════════════════════════════════ */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        <div className="grid grid-cols-2 gap-x-10 gap-y-3">
          {/* ── LEFT COLUMN ── */}
          <div className="space-y-3">
            {/* Type */}
            <Field label="Type" disabled={isLocked}>
              <select value={form.prod_type} disabled={isLocked}
                onChange={e => set('prod_type', e.target.value)}
                className={`flex-1 ${sel} ${isLocked ? dis : ''}`}>
                {TYPE_OPTS.map(o => <option key={o}>{o}</option>)}
              </select>
            </Field>

            {/* Status */}
            <Field label="Status" disabled={isLocked}>
              <select value={form.status} disabled={isLocked}
                onChange={e => set('status', e.target.value)}
                className={`flex-1 ${sel} ${isLocked ? dis : ''}`}>
                {STATUS_OPTS.map(s => <option key={s}>{s}</option>)}
              </select>
            </Field>

            {/* Product No. — searchable */}
            <div className="flex items-start gap-2">
              <label className={`${lbl} pt-2`}>Product No. *</label>
              <div className="flex-1 relative">
                <input value={itemSearch} disabled={isLocked}
                  onChange={e => {
                    setItemSearch(e.target.value);
                    set('item_no', ''); set('item_id', ''); set('description', '');
                    setShowItemDrop(true);
                    searchItems(e.target.value);
                  }}
                  onFocus={() => { if (items.length) setShowItemDrop(true); }}
                  placeholder="Search item no or name…"
                  className={`${inp} ${isLocked ? dis : 'bg-white'}`} />
                {showItemDrop && !form.item_no && filteredItems.length > 0 && (
                  <div className="absolute z-20 w-full border border-sap-border rounded-lg mt-1 max-h-44 overflow-y-auto shadow-lg bg-white">
                    {filteredItems.slice(0, 10).map(it => (
                      <button key={it.item_id} onClick={() => pickItem(it)}
                        className="w-full px-3 py-2 text-left text-xs hover:bg-sap-blueLight transition-colors
                                   border-b border-sap-border/40 last:border-0">
                        <span className="font-semibold text-sap-blue">{it.item_no}</span>
                        <span className="text-sap-textLight ml-1.5">{it.description}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Product Description */}
            <Field label="Product Description" value={form.description} disabled />

            {/* Planned Quantity + UoM Name */}
            <div className="flex items-center gap-2">
              <label className={lbl}>Planned Quantity *</label>
              <div className="flex-1 flex items-center gap-2">
                <input type="text" value={form.planned_qty} disabled={isLocked}
                  onChange={e => {
                    const newQty = e.target.value;
                    set('planned_qty', newQty);
                    if (isNew && components.length > 0) {
                      const q = Number(newQty) || 0;
                      setComponents(prev => prev.map(c => {
                        const ratio = Number(c.base_ratio) || 1;
                        const bq = Number(c.base_qty) || 0;
                        const pq = ratio ? (q * bq) / ratio : 0;
                        return { ...c, planned_qty: pq, total_cost: pq * (Number(c.unit_cost) || 0) };
                      }));
                    }
                  }}
                  className={`w-1/2 ${inp} ${isLocked ? dis : 'bg-white'}`} />
                <label className="text-xs text-sap-textLight shrink-0">UoM Name</label>
                <input type="text" value={form.uom_name} disabled={isLocked}
                  onChange={e => set('uom_name', e.target.value)}
                  className={`w-1/2 ${inp} ${isLocked ? dis : 'bg-white'}`} />
              </div>
            </div>

            {/* Warehouse */}
            <Field label="Warehouse" disabled={isLocked}>
              <select value={form.warehouse_id} disabled={isLocked}
                onChange={e => set('warehouse_id', e.target.value)}
                className={`flex-1 ${sel} ${isLocked ? dis : ''}`}>
                <option value="">— Select —</option>
                {warehouses.map(w => (
                  <option key={w.warehouse_id} value={w.warehouse_id}>{w.wh_code} — {w.wh_name}</option>
                ))}
              </select>
            </Field>

            {/* Priority */}
            <Field label="Priority" type="number" value={form.priority}
              onChange={v => set('priority', v)} disabled={isLocked} />

            {/* Routing Date Calculation + Update Now */}
            <div className="flex items-center gap-2">
              <label className={lbl}>Routing Date Calculation</label>
              <select value={form.routing_date_calc} disabled={isLocked}
                onChange={e => set('routing_date_calc', e.target.value)}
                className={`flex-1 ${sel} ${isLocked ? dis : ''}`}>
                {ROUTING_OPTS.map(o => <option key={o}>{o}</option>)}
              </select>
              <button disabled={isLocked}
                className="px-4 py-1.5 text-xs font-semibold bg-sap-gold text-white rounded-lg
                           hover:bg-[#B3853F] disabled:opacity-40 whitespace-nowrap shadow-sm">
                Update Now
              </button>
            </div>

            {/* Procure Items checkbox */}
            <div className="flex items-center gap-2">
              <label className={lbl}></label>
              <label className="flex items-center gap-2 text-xs text-sap-text cursor-pointer">
                <input type="checkbox" checked={form.procure_items || false} disabled={isLocked}
                  onChange={e => set('procure_items', e.target.checked)}
                  className="w-4 h-4 rounded border-sap-border text-sap-blue focus:ring-sap-blue/20" />
                Procure Items
              </label>
            </div>

            {/* Department (optional) */}
            <Field label="Document Series">
              <select value={form.department || ''} disabled={isLocked}
                onChange={e => set('department', e.target.value)}
                className={`flex-1 ${sel} ${isLocked ? dis : ''}`}>
                <option value="">— Select —</option>
                {departmentOpts.map((d) => (
                  <option key={d} value={d}>{d}</option>
                ))}
              </select>
            </Field>
          </div>

          {/* ── RIGHT COLUMN ── */}
          <div className="space-y-3">
            {/* No. (Order Number) */}
            <div className="flex items-center gap-2">
              <label className={lbl}>No.</label>
              <div className="flex-1 flex items-center gap-2">
                {isNew ? (
                  <div className="relative w-1/2">
                    <select value={form.series_id || ''}
                      onChange={e => handleSeriesChange(e.target.value)}
                      className={`${inp} bg-white pr-6 appearance-none`}>
                      {series.map(s => (
                        <option key={s.series_id} value={s.series_id}>{s.series_name}</option>
                      ))}
                    </select>
                    <svg className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-sap-textLight" width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </div>
                ) : (
                  <input value={form.series || 'Primary'} readOnly
                    className={`w-1/2 ${inp} bg-gray-50 text-sap-textLight`} />
                )}
                <input value={form.order_no} readOnly
                  className={`w-1/2 ${inp} bg-gray-50 text-sap-textLight`} />
              </div>
            </div>

            {/* Order Date */}
            <Field label="Order Date" type="date" value={form.order_date}
              onChange={v => set('order_date', v)} disabled={isLocked} />

            {/* Start Date */}
            <Field label="Start Date" type="date" value={form.start_date}
              onChange={v => set('start_date', v)} disabled={isLocked} />

            {/* Due Date */}
            <Field label="Due Date *" type="date" value={form.due_date}
              onChange={v => set('due_date', v)} disabled={isLocked} />

            {/* User */}
            <Field label="User" disabled={isLocked}>
              <select value={form.user} disabled={isLocked}
                onChange={e => set('user', e.target.value)}
                className={`flex-1 ${sel} ${isLocked ? dis : ''}`}>
                <option value="">— Select —</option>
                {users.map(u => <option key={u.user_id} value={u.name}>{u.name}</option>)}
              </select>
            </Field>

            {/* Origin */}
            <Field label="Origin" disabled={isLocked}>
              <select value={form.origin} disabled={isLocked}
                onChange={e => set('origin', e.target.value)}
                className={`flex-1 ${sel} ${isLocked ? dis : ''}`}>
                {ORIGIN_OPTS.map(o => <option key={o}>{o}</option>)}
              </select>
            </Field>

            {/* Linked To */}
            <Field label="Linked To" disabled={isLocked}>
              <select value={form.linked_to || ''} disabled={isLocked}
                onChange={e => set('linked_to', e.target.value)}
                className={`flex-1 ${sel} ${isLocked ? dis : ''}`}>
                {LINKED_TO_OPTS.map(o => <option key={o} value={o}>{o || '— None —'}</option>)}
              </select>
            </Field>

            {/* Linked Order */}
            <Field label="Linked Order" value={form.linked_so_id}
              onChange={v => set('linked_so_id', v)} type="number" disabled={isLocked} />

            {/* Customer */}
            <Field label="Customer" value={form.customer}
              onChange={v => set('customer', v)} disabled={isLocked} />

            {/* Subcontractor (job-worker — used on delivery challan) */}
            <Field label="Subcontractor" disabled={isLocked}>
              <div className="relative flex-1">
                {form.subcontractor_vendor_id ? (
                  <div className={`${inp} flex items-center gap-2 min-h-8`}>
                    <span className="text-sap-text truncate flex-1">{subcontractorLabel || `Vendor #${form.subcontractor_vendor_id}`}</span>
                    <button
                      type="button"
                      onClick={() => !isLocked && clearSubcontractor()}
                      className="text-red-400 hover:text-red-600 text-xs shrink-0"
                      title="Clear subcontractor">
                      ✕
                    </button>
                  </div>
                ) : (
                  <div className="flex items-stretch w-full">
                    <input
                      value={subcontractorSearch}
                      placeholder="Search subcontractor…"
                      onChange={(e) => { setSubcontractorSearch(e.target.value); setShowSubcontractorDD(true); }}
                      onFocus={() => setShowSubcontractorDD(true)}
                      onBlur={() => setTimeout(() => setShowSubcontractorDD(false), 200)}
                      disabled={isLocked}
                      autoComplete="off"
                      className="flex-1 min-w-0 h-8 px-3 text-xs border border-sap-border rounded-l-lg bg-white border-r-0
                                 focus:outline-none focus:ring-1 focus:ring-sap-blue/20 focus:border-sap-blue focus:z-10"
                    />
                    <button
                      type="button"
                      title="Show vendors"
                      aria-label="Open subcontractor list"
                      disabled={isLocked}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        setShowSubcontractorDD(true);
                      }}
                      className="h-8 w-8 shrink-0 flex items-center justify-center border border-sap-border rounded-r-lg
                                 bg-gray-50 text-sap-textLight hover:bg-sap-blue/10 hover:text-sap-blue hover:border-sap-blue/40 transition-colors disabled:opacity-40">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </div>
                )}
                {showSubcontractorDD && !form.subcontractor_vendor_id && (
                  <div className="absolute z-50 left-0 top-full mt-0.5 w-full bg-white border border-sap-border rounded-lg shadow-xl max-h-48 overflow-y-auto">
                    {subcontractorListLoading && (
                      <div className="px-3 py-3 text-xs text-sap-textLight text-center">Loading…</div>
                    )}
                    {!subcontractorListLoading && subcontractorResults.length === 0 && (
                      <div className="px-3 py-2 text-xs text-sap-textLight">No vendors found</div>
                    )}
                    {!subcontractorListLoading && subcontractorResults.map((v) => (
                      <button
                        key={v.vendor_id}
                        type="button"
                        onMouseDown={(e) => { e.preventDefault(); pickSubcontractor(v); }}
                        className="w-full text-left px-3 py-1.5 text-xs hover:bg-sap-blue/10 flex items-center gap-2 border-b border-sap-border/30 last:border-0">
                        <span className="font-semibold text-sap-blue">{v.vendor_code}</span>
                        <span className="text-sap-textLight truncate">{v.vendor_name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </Field>

            {/* Distr. Rule */}
            <div className="flex items-center gap-2">
              <label className={lbl}>Distr. Rule</label>
              <div className="flex-1 flex items-center gap-1">
                <input type="text" value={form.distr_rule ?? ''} disabled={isLocked}
                  onChange={e => set('distr_rule', e.target.value)}
                  className={`flex-1 ${inp} ${isLocked ? dis : 'bg-white'}`} />
              </div>
              <YellowArrow onClick={() => setDistPickerOpen(true)} title="Distribution Rule" disabled={isLocked} />
            </div>

            {/* Project */}
            <div className="flex items-center gap-2">
              <label className={lbl}>Project</label>
              <div className="flex-1">
                <ProjectField value={form.project_no} onChange={v => set('project_no', v)} disabled={isLocked} />
              </div>
              <button type="button" title="Open Project"
                className="flex-shrink-0 flex items-center justify-center w-5 h-5 hover:opacity-80 transition-opacity">
                <svg width="12" height="12" viewBox="0 0 14 14" className="drop-shadow-sm">
                  <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* ═══════════════════════════════════════════════════════
          TABS — Components | Summary | Attachments  (SAP B1)
          ═══════════════════════════════════════════════════════ */}
      <div className="flex gap-1 border-b border-sap-border overflow-x-auto">
        {[
          ['components', `Components (${components.length})`],
          ['summary', 'Summary'],
          ['attachments', `Attachments (${attachments.length})`],
        ].map(([key, label]) => (
          <button key={key} onClick={() => setTab(key)}
            className={`px-4 py-2 text-xs font-semibold rounded-t-lg transition-colors whitespace-nowrap
              ${tab === key
                ? 'bg-white text-sap-blue border-t border-x border-sap-border -mb-px'
                : 'text-sap-textLight hover:text-sap-text hover:bg-white/50'}`}>
            {label}
          </button>
        ))}
      </div>

      {/* ── Tab content ── */}
      <div className="bg-white rounded-2xl shadow-sm p-6">
        {tab === 'components' ? (
          <ComponentsTab orderId={id} components={components} setComponents={setComponents}
            isLocked={isLocked} isNew={isNew} warehouses={warehouses}
            headerPlannedQty={form.planned_qty} />
        ) : tab === 'summary' ? (
          <SummaryTab form={form} set={set} isLocked={isLocked} isOverdue={isOverdue} onRefClick={() => setShowRefInfo(true)} />
        ) : (
          <AttachmentsTab orderId={id} attachments={attachments} setAttachments={setAttachments}
            isLocked={isLocked} isNew={isNew} />
        )}
      </div>

      {/* ── Bottom: Remarks ── */}
      <div className="bg-white rounded-2xl shadow-sm p-6 space-y-3">
        <div className="grid grid-cols-2 gap-x-10 gap-y-3">
          <div>
            <label className="text-xs text-sap-textLight block mb-1">Remarks</label>
            <textarea rows={3} value={form.remarks || ''} disabled={isLocked}
              onChange={e => set('remarks', e.target.value)}
              className={`w-full px-3 py-2 text-xs border border-sap-border rounded-lg focus:outline-none
                         focus:border-sap-blue focus:ring-1 focus:ring-sap-blue/20
                         ${isLocked ? dis : 'bg-white'}`} />
          </div>
          <div>
            <label className="text-xs text-sap-textLight block mb-1">Pick and Pack Remarks</label>
            <textarea rows={3} value={form.pick_pack_remarks || ''} disabled={isLocked}
              onChange={e => set('pick_pack_remarks', e.target.value)}
              className={`w-full px-3 py-2 text-xs border border-sap-border rounded-lg focus:outline-none
                         focus:border-sap-blue focus:ring-1 focus:ring-sap-blue/20
                         ${isLocked ? dis : 'bg-white'}`} />
          </div>
        </div>

        {/* ── Add / Cancel buttons (SAP B1 bottom placement) ── */}
        <div className="flex items-center gap-2 mt-4">
          <button onClick={handleSave} disabled={saving || isLocked}
            className="px-6 py-2 text-xs font-semibold bg-sap-blue text-white rounded-lg
                       hover:bg-sap-blueDark disabled:opacity-40 shadow-sm transition-all">
            {saving ? 'Saving…' : isNew ? 'Add' : 'Update'}
          </button>
          <button onClick={() => navigate('/production/orders')}
            className="px-6 py-2 text-xs font-semibold bg-gray-500 text-white rounded-lg
                       hover:bg-gray-600 transition-all">
            Cancel
          </button>
        </div>
      </div>
      {/* ── Reference Information dialog ── */}
      {showRefInfo && (
        <ReferenceInfoDialog
          refsTo={refsTo} refsBy={refsBy}
          onSave={({ refsTo: t, refsBy: b }) => { setRefsTo(t); setRefsBy(b); }}
          onClose={() => setShowRefInfo(false)}
        />
      )}
      {/* ── Relationship Map ── */}
      {showRelMap && (
        <RelationshipMapModal
          form={form}
          issues={relMapIssues}
          receipts={relMapReceipts}
          loading={relMapLoading}
          onClose={() => setShowRelMap(false)}
        />
      )}

      {distPickerOpen && (
        <SapReferencePickerDialog
          type="distribution"
          current={form.distr_rule || ''}
          onSelect={(row) => {
            set('distr_rule', row.rule_code || '');
            setDistPickerOpen(false);
          }}
          onClose={() => setDistPickerOpen(false)}
        />
      )}

      {showGeneralModal && (
        <GeneralModalDialog
          form={form}
          components={components}
          subcontractorLabel={subcontractorLabel}
          onClose={() => setShowGeneralModal(false)}
        />
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Components Tab — SAP B1 exact columns
   ═══════════════════════════════════════════════════════════════ */
function ComponentsTab({ orderId, components, setComponents, isLocked, isNew, warehouses, headerPlannedQty }) {
  const navigate = useNavigate();
  const [showMrpImport, setShowMrpImport] = useState(false);
  const [importingMrp, setImportingMrp] = useState(false);
  const [addModal, setAddModal] = useState(false);
  const [addForm, setAddForm]   = useState({ line_type: 'Item', item_id: '', resource_id: '', description: '', planned_qty: 1, unit_cost: 0, uom: 'NOS', warehouse_id: '', issue_method: 'Manual', base_qty: 1, base_ratio: 1 });
  const [saving, setSaving]     = useState(false);
  const [itemOptions, setItemOptions] = useState([]);
  const [resOptions, setResOptions]   = useState([]);
  const [itemSearch, setItemSearch]   = useState('');
  const [resSearch, setResSearch]     = useState('');
  const [showItemDrop, setShowItemDrop] = useState(false);
  const [showResDrop, setShowResDrop]   = useState(false);

  useEffect(() => {
    if (addForm.line_type !== 'Item' || !addModal) return;
    const t = setTimeout(() => {
      api.get(`/items?search=${encodeURIComponent(itemSearch)}&limit=20`)
        .then(r => setItemOptions(Array.isArray(r) ? r : (r.rows || r.data || [])))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [itemSearch, addForm.line_type, addModal]);

  useEffect(() => {
    if (addForm.line_type !== 'Resource' || !addModal) return;
    const t = setTimeout(() => {
      api.get(`/resources?search=${encodeURIComponent(resSearch)}`)
        .then(r => setResOptions(Array.isArray(r) ? r : (r.rows || r.data || [])))
        .catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [resSearch, addForm.line_type, addModal]);

  const pickItem = (item) => {
    setAddForm(f => ({ ...f, item_id: item.item_id, description: item.description, unit_cost: Number(item.unit_price || 0) }));
    setItemSearch(`${item.item_no} — ${item.description}`);
    setShowItemDrop(false);
  };

  const pickResource = (res) => {
    setAddForm(f => ({ ...f, resource_id: res.resource_id, description: res.description, unit_cost: Number(res.std_cost || 0) }));
    setResSearch(`${res.resource_no} — ${res.description}`);
    setShowResDrop(false);
  };

  const openModal = () => {
    setAddForm({ line_type: 'Item', item_id: '', resource_id: '', description: '', planned_qty: 1, unit_cost: 0, uom: 'NOS', warehouse_id: '', issue_method: 'Manual', base_qty: 1, base_ratio: 1 });
    setItemSearch(''); setResSearch('');
    setAddModal(true);
  };

  const addComponent = async () => {
    setSaving(true);
    try {
      if (isNew) {
        const pq = Number(addForm.planned_qty) || 0;
        const uc = Number(addForm.unit_cost) || 0;
        setComponents(prev => [...prev, {
          comp_id: `local-${Date.now()}`,
          line_type: addForm.line_type,
          item_id: addForm.line_type === 'Item' ? (addForm.item_id || null) : null,
          item_no: addForm.line_type === 'Item' ? (itemSearch.split(' — ')[0] || '') : null,
          resource_id: addForm.line_type === 'Resource' ? (addForm.resource_id || null) : null,
          resource_no: addForm.line_type === 'Resource' ? (resSearch.split(' — ')[0] || '') : null,
          description: addForm.description || '',
          base_qty: Number(addForm.base_qty) || 1,
          base_ratio: Number(addForm.base_ratio) || 1,
          planned_qty: pq,
          uom: addForm.uom || 'NOS',
          warehouse_id: addForm.warehouse_id || null,
          issue_method: addForm.issue_method || 'Manual',
          unit_cost: uc,
          total_cost: pq * uc,
          issued_qty: 0,
        }]);
        setAddModal(false);
      } else {
        await api.post(`/production-orders/${orderId}/components`, {
          ...addForm,
          planned_qty: Number(addForm.planned_qty),
          unit_cost: Number(addForm.unit_cost),
          base_qty: Number(addForm.base_qty) || 1,
          base_ratio: Number(addForm.base_ratio) || 1,
          item_id: addForm.line_type === 'Item' ? (addForm.item_id || null) : null,
          resource_id: addForm.line_type === 'Resource' ? (addForm.resource_id || null) : null,
          warehouse_id: addForm.warehouse_id || null,
        });
        const d = await api.get(`/production-orders/${orderId}`);
        setComponents(d.components || []);
        setAddModal(false);
      }
    } catch (e) { alert(e.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const removeComp = async (compId) => {
    if (!window.confirm('Remove this component?')) return;
    if (String(compId).startsWith('bom-')) {
      setComponents(prev => prev.filter(c => c.comp_id !== compId));
      return;
    }
    try {
      await api.delete(`/production-orders/${orderId}/components/${compId}`);
      setComponents(prev => prev.filter(c => c.comp_id !== compId));
    } catch (e) { alert(e.message || 'Failed'); }
  };

  const applyMrpComponents = async (recs, _run) => {
    const H0 = Number(headerPlannedQty) || 1;
    const mapRecToLocal = (rec) => {
      const R = Number(rec.recommended_qty) || 0;
      const uc = Number(rec.unit_price) || 0;
      const baseQty = H0 ? R / H0 : R;
      return {
        comp_id: `mrp-${rec.rec_id}-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        line_type: 'Item',
        item_id: rec.item_id,
        item_no: rec.item_no || '',
        description: rec.item_desc || '',
        base_qty: baseQty,
        base_ratio: 1,
        planned_qty: R,
        uom: rec.uom_name || rec.uom || 'NOS',
        warehouse_id: rec.to_warehouse_id || null,
        issue_method: 'Manual',
        unit_cost: uc,
        total_cost: R * uc,
        issued_qty: 0,
      };
    };

    if (isNew) {
      setComponents((prev) => [...prev, ...recs.map(mapRecToLocal)]);
      return;
    }

    setImportingMrp(true);
    try {
      for (const rec of recs) {
        const R = Number(rec.recommended_qty) || 0;
        await api.post(`/production-orders/${orderId}/components`, {
          line_type: 'Item',
          item_id: rec.item_id,
          resource_id: null,
          description: rec.item_desc || '',
          planned_qty: R,
          unit_cost: Number(rec.unit_price) || 0,
          uom: rec.uom_name || rec.uom || 'NOS',
          warehouse_id: rec.to_warehouse_id || null,
          issue_method: 'Manual',
          base_qty: 1,
          base_ratio: 1,
        });
      }
      const d = await api.get(`/production-orders/${orderId}`);
      setComponents(d.components || []);
    } catch (e) {
      alert(e.message || 'Import failed');
    } finally {
      setImportingMrp(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="text-sm font-bold text-sap-text">Components</h3>
        {!isLocked && (
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={() => setShowMrpImport(true)}
              disabled={importingMrp}
              className="px-3 py-1.5 text-xs font-semibold border border-sap-blue text-sap-blue rounded-lg bg-white hover:bg-sap-blue/5 disabled:opacity-50 whitespace-nowrap">
              {importingMrp ? 'Importing…' : 'Import from order recommendation'}
            </button>
            <button onClick={openModal}
              className="btn-primary-sm">
              + Add Component
            </button>
          </div>
        )}
      </div>

      {/* SAP B1 Components Table — all 19 columns */}
      <div className="rounded-2xl shadow-sm overflow-x-auto">
        <table className="w-full text-xs min-w-[1800px]">
          <thead>
            <tr className="bg-gray-50 border-b border-sap-border">
              <th className={`${thCls} w-8 text-center`}>#</th>
              <th className={`${thCls} w-16`}>Type</th>
              <th className={`${thCls} w-28`}>No.</th>
              <th className={`${thCls} min-w-[140px]`}>Description</th>
              <th className={`${thCls} w-20 text-right`}>Base Qty</th>
              <th className={`${thCls} w-20 text-right`}>Base Ratio</th>
              <th className={`${thCls} w-24 text-right`}>Planned Qty</th>
              <th className={`${thCls} w-20 text-right`}>Issued</th>
              <th className={`${thCls} w-20 text-right`}>Available</th>
              <th className={`${thCls} w-20`}>UoM Code</th>
              <th className={`${thCls} w-20`}>UoM Name</th>
              <th className={`${thCls} w-24`}>Warehouse</th>
              <th className={`${thCls} w-24`}>Issue Method</th>
              <th className={`${thCls} w-24`}>WIP Account</th>
              <th className={`${thCls} w-20`}>Standort</th>
              <th className={`${thCls} w-28`}>Produktgruppe</th>
              <th className={`${thCls} w-28`}>Route Sequence</th>
              <th className={`${thCls} w-32`}>Procurement Doc.</th>
              <th className={`${thCls} w-36`}>Allow Procurmt Doc.</th>
              {!isLocked && <th className={`${thCls} w-8`}></th>}
            </tr>
          </thead>
          <tbody>
            {components.length === 0 ? (
              <tr>
                <td colSpan={isLocked ? 19 : 20} className="px-4 py-12 text-center text-xs text-sap-textLight italic">
                  No components added yet.
                </td>
              </tr>
            ) : (
              components.map((c, i) => (
                <tr key={c.comp_id}
                  className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F3]'} border-b border-sap-border/60`}>
                  <td className="px-3 py-2 text-center text-sap-textLight">{i + 1}</td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-2xs font-semibold
                      ${c.line_type === 'Item' ? 'bg-blue-50 text-blue-700' : 'bg-amber-50 text-amber-700'}`}>
                      {c.line_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 font-semibold text-sap-blue">
                    <div className="flex items-center gap-1">
                      <span>{c.line_type === 'Item' ? (c.item_no || c.item_id) : (c.resource_no || c.resource_id)}</span>
                      {c.line_type === 'Item' && c.item_no && (
                        <YellowArrow
                          title={`Open Item Master for ${c.item_no}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/item-master/${encodeURIComponent(c.item_no)}`); }}
                        />
                      )}
                      {c.line_type === 'Resource' && (c.resource_id || c.resource_no) && (
                        <YellowArrow
                          title={`Open Resource Master for ${c.resource_no || c.resource_id}`}
                          onClick={(e) => { e.stopPropagation(); navigate(`/resource/${c.resource_id || c.resource_no}`); }}
                        />
                      )}
                    </div>
                  </td>
                  <td className="px-3 py-2 text-sap-text">{c.item_desc || c.resource_desc || c.description}</td>
                  <td className="px-3 py-2 text-right text-sap-textLight">{Number(c.base_qty || 1)}</td>
                  <td className="px-3 py-2 text-right text-sap-textLight">{Number(c.base_ratio || 1)}</td>
                  <td className="px-3 py-2 text-right font-semibold">{Number(c.planned_qty)}</td>
                  <td className="px-3 py-2 text-right">{Number(c.issued_qty || 0)}</td>
                  <td className="px-3 py-2 text-right">{Number(c.available_qty || 0)}</td>
                  <td className="px-3 py-2 text-sap-textLight">{c.uom || '—'}</td>
                  <td className="px-3 py-2 text-sap-textLight">{c.uom || '—'}</td>
                  <td className="px-3 py-2 text-sap-textLight">{c.comp_wh_code || '—'}</td>
                  <td className="px-3 py-2">{c.issue_method || '—'}</td>
                  <td className="px-3 py-2 text-sap-textLight">—</td>
                  <td className="px-3 py-2 text-sap-textLight">—</td>
                  <td className="px-3 py-2 text-sap-textLight">—</td>
                  <td className="px-3 py-2 text-sap-textLight">—</td>
                  <td className="px-3 py-2 text-sap-textLight">—</td>
                  <td className="px-3 py-2 text-sap-textLight">—</td>
                  {!isLocked && (
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => removeComp(c.comp_id)} className="text-red-400 hover:text-red-600">✕</button>
                    </td>
                  )}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Add Component Modal ── */}
      {addModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setAddModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-sap-text mb-4">Add Component</h3>
            <div className="space-y-3">
              {/* Type */}
              <div>
                <label className="text-xs text-sap-textLight mb-1 block">Type</label>
                <select value={addForm.line_type}
                  onChange={e => { setAddForm({...addForm, line_type: e.target.value, item_id: '', resource_id: '', description: '', unit_cost: 0}); setItemSearch(''); setResSearch(''); }}
                  className={`w-full ${sel}`}>
                  <option value="Item">Item</option><option value="Resource">Resource</option>
                </select>
              </div>

              {/* Searchable picker */}
              {addForm.line_type === 'Item' ? (
                <div className="relative">
                  <label className="text-xs text-sap-textLight mb-1 block">Search Item</label>
                  <input value={itemSearch} placeholder="Type item no. or name…"
                    onChange={e => { setItemSearch(e.target.value); setShowItemDrop(true); setAddForm(f => ({...f, item_id: '', description: ''})); }}
                    onFocus={() => setShowItemDrop(true)}
                    className={`w-full ${inp} bg-white`} />
                  {showItemDrop && itemOptions.length > 0 && (
                    <div className="absolute z-20 top-full left-0 right-0 bg-white border border-sap-border rounded-lg shadow-lg max-h-48 overflow-auto mt-0.5">
                      {itemOptions.map(it => (
                        <div key={it.item_id} onClick={() => pickItem(it)}
                          className="px-3 py-2 text-xs hover:bg-sap-blueLight cursor-pointer border-b border-sap-border/40 last:border-0">
                          <span className="font-semibold text-sap-blue">{it.item_no}</span>
                          <span className="text-sap-textLight ml-1.5">{it.description}</span>
                          <span className="text-sap-textLight float-right">₹{Number(it.unit_price || 0).toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {addForm.item_id && <p className="text-[10px] text-green-600 mt-0.5">✓ Selected: {addForm.description}</p>}
                </div>
              ) : (
                <div className="relative">
                  <label className="text-xs text-sap-textLight mb-1 block">Search Resource</label>
                  <input value={resSearch} placeholder="Type resource no. or name…"
                    onChange={e => { setResSearch(e.target.value); setShowResDrop(true); setAddForm(f => ({...f, resource_id: '', description: ''})); }}
                    onFocus={() => setShowResDrop(true)}
                    className={`w-full ${inp} bg-white`} />
                  {showResDrop && resOptions.length > 0 && (
                    <div className="absolute z-20 top-full left-0 right-0 bg-white border border-sap-border rounded-lg shadow-lg max-h-48 overflow-auto mt-0.5">
                      {resOptions.map(r => (
                        <div key={r.resource_id} onClick={() => pickResource(r)}
                          className="px-3 py-2 text-xs hover:bg-amber-50 cursor-pointer border-b border-sap-border/40 last:border-0">
                          <span className="font-semibold text-amber-700">{r.resource_no}</span>
                          <span className="text-sap-textLight ml-1.5">{r.description}</span>
                          <span className="text-sap-textLight float-right">₹{Number(r.std_cost || 0).toLocaleString('en-IN')}</span>
                        </div>
                      ))}
                    </div>
                  )}
                  {addForm.resource_id && <p className="text-[10px] text-green-600 mt-0.5">✓ Selected: {addForm.description}</p>}
                </div>
              )}

              {/* Description (readonly) */}
              <div>
                <label className="text-xs text-sap-textLight mb-1 block">Description</label>
                <input value={addForm.description} readOnly className={`w-full ${inp} bg-gray-50`} />
              </div>

              {/* Row: Base Qty, Base Ratio, Planned Qty */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-sap-textLight mb-1 block">Base Qty</label>
                  <input type="number" value={addForm.base_qty} onChange={e => setAddForm({...addForm, base_qty: e.target.value})}
                    className={`w-full ${inp} bg-white`} />
                </div>
                <div>
                  <label className="text-xs text-sap-textLight mb-1 block">Base Ratio</label>
                  <input type="number" value={addForm.base_ratio} onChange={e => setAddForm({...addForm, base_ratio: e.target.value})}
                    className={`w-full ${inp} bg-white`} />
                </div>
                <div>
                  <label className="text-xs text-sap-textLight mb-1 block">Planned Qty</label>
                  <input type="number" value={addForm.planned_qty} onChange={e => setAddForm({...addForm, planned_qty: e.target.value})}
                    className={`w-full ${inp} bg-white`} />
                </div>
              </div>

              {/* Row: UoM, Issue Method, Unit Cost */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-xs text-sap-textLight mb-1 block">UoM</label>
                  <select value={addForm.uom} onChange={e => setAddForm({...addForm, uom: e.target.value})}
                    className={`w-full ${sel}`}>
                    {UOM_OPTS.map(u => <option key={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-sap-textLight mb-1 block">Issue Method</label>
                  <select value={addForm.issue_method} onChange={e => setAddForm({...addForm, issue_method: e.target.value})}
                    className={`w-full ${sel}`}>
                    {ISSUE_OPTS.map(m => <option key={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-sap-textLight mb-1 block">Unit Cost (₹)</label>
                  <input type="number" value={addForm.unit_cost} onChange={e => setAddForm({...addForm, unit_cost: e.target.value})}
                    className={`w-full ${inp} bg-white`} />
                </div>
              </div>

              {/* Warehouse */}
              <div>
                <label className="text-xs text-sap-textLight mb-1 block">Warehouse</label>
                <select value={addForm.warehouse_id} onChange={e => setAddForm({...addForm, warehouse_id: e.target.value})}
                  className={`w-full ${sel}`}>
                  <option value="">— None —</option>
                  {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.wh_code} — {w.wh_name}</option>)}
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setAddModal(false)} className="px-4 py-2 text-xs border border-sap-border rounded-lg hover:bg-white">Cancel</button>
              <button onClick={addComponent} disabled={saving || (!addForm.item_id && !addForm.resource_id)}
                className="px-4 py-2 text-xs font-semibold bg-sap-blue text-white rounded-lg disabled:opacity-40 hover:bg-sap-blueDark">
                {saving ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showMrpImport && (
        <MrpRecommendationImportDialog
          open={showMrpImport}
          onClose={() => setShowMrpImport(false)}
          mode="production"
          onApply={(recs, run) => { void applyMrpComponents(recs, run); }}
        />
      )}

    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Summary Tab — SAP B1 exact 3-column layout
   Costs | Quantities + Dates | Planned Times + Planned Days
   ═══════════════════════════════════════════════════════════════ */
function SummaryTab({ form, set, isLocked, isOverdue, onRefClick }) {
  const fmt = (v) => `₹${Number(v || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;

  return (
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-x-8 gap-y-4">
      {/* ── Column 1: Costs ── */}
      <div>
        <h4 className="text-xs font-bold text-sap-blue underline mb-3">Costs</h4>
        <div className="space-y-2">
          <SummaryRow label="Actual Item Component Cost" value={fmt(form.total_material_cost)} />
          <SummaryRow label="Actual Resource Component Cost" value={fmt(form.total_resource_cost)} />
          <SummaryRow label="Actual Additional Cost" value={fmt(0)} />
          <div className="flex items-center gap-2">
            <label className="w-52 text-xs text-sap-text font-bold text-right shrink-0">Actual Product Cost</label>
            <span className="flex-1 h-8 flex items-center px-3 text-xs border border-sap-border rounded-lg bg-gray-100 font-semibold text-sap-text">
              {fmt(form.total_cost)}
            </span>
          </div>
          <SummaryRow label="Actual By-Product Cost" value={fmt(0)} />
          <SummaryRow label="Total Variance"
            value={fmt(Number(form.total_cost || 0) - (Number(form.total_material_cost || 0) + Number(form.total_resource_cost || 0)))} />
          <div className="flex items-center gap-2">
            <label className="w-52 text-xs text-sap-textLight text-right shrink-0">Journal Remark</label>
            <input value={form.journal_remark || ''} disabled={isLocked}
              onChange={e => set('journal_remark', e.target.value)}
              className={`flex-1 ${inp} ${isLocked ? dis : 'bg-white'}`} />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-52 text-xs text-sap-textLight text-right shrink-0">Referenced Document</label>
            <div className="flex-1 flex items-center">
              <button onClick={onRefClick}
                className="px-3 h-8 text-xs border border-sap-border rounded-lg bg-white hover:bg-gray-50 font-bold">…</button>
            </div>
          </div>
        </div>
      </div>

      {/* ── Column 2: Quantities + Dates ── */}
      <div>
        <h4 className="text-xs font-bold text-sap-blue underline mb-3">Quantities</h4>
        <div className="space-y-2">
          <SummaryRow label="Planned Quantity" value={Number(form.planned_qty || 0)} />
          <div className="flex items-center gap-2">
            <label className="w-52 text-xs text-sap-textLight text-right shrink-0">Completed Quantity</label>
            <input type="number" value={form.completed_qty ?? 0} disabled={isLocked}
              onChange={e => set('completed_qty', e.target.value)}
              className={`flex-1 ${inp} ${isLocked ? dis : 'bg-white'}`} />
          </div>
          <div className="flex items-center gap-2">
            <label className="w-52 text-xs text-sap-textLight text-right shrink-0">Rejected Quantity</label>
            <input type="number" value={form.rejected_qty ?? 0} disabled={isLocked}
              onChange={e => set('rejected_qty', e.target.value)}
              className={`flex-1 ${inp} ${isLocked ? dis : 'bg-white'}`} />
          </div>
        </div>

        <h4 className="text-xs font-bold text-sap-blue underline mb-3 mt-6">Dates</h4>
        <div className="space-y-2">
          <SummaryRow label="Due Date" value={form.due_date || '—'} />
          <div className="flex items-center gap-2">
            <label className="w-52 text-xs text-sap-textLight text-right shrink-0">Actual Closing Date</label>
            <input type="date" value={form.actual_closing_date || ''} disabled={isLocked}
              onChange={e => set('actual_closing_date', e.target.value)}
              className={`flex-1 ${inp} ${isLocked ? dis : 'bg-white'}`} />
          </div>
          <SummaryRow label="Overdue"
            value={isOverdue ? 'Yes' : 'No'}
            valueClass={isOverdue ? '!text-red-600 font-semibold' : '!text-green-600'} />
        </div>
      </div>

      {/* ── Column 3: Planned Times + Planned Days ── */}
      <div>
        <h4 className="text-xs font-bold text-sap-blue underline mb-3">Planned Times</h4>
        <div className="space-y-2">
          <SummaryRow label="Total Production Time" value="0.00" />
          <SummaryRow label="Total Additional Time" value="0.00" />
          <SummaryRow label="Total Run Time" value="0.00" />
        </div>

        <h4 className="text-xs font-bold text-sap-blue underline mb-3 mt-6">Planned Days</h4>
        <div className="space-y-2">
          <SummaryRow label="Total Required Days" value={
            form.start_date && form.due_date
              ? Math.max(0, Math.ceil((new Date(form.due_date) - new Date(form.start_date)) / 86400000))
              : 0
          } />
          <SummaryRow label="Total Waiting Days" value="0" />
          <SummaryRow label="Total Days" value={
            form.start_date && form.due_date
              ? Math.max(0, Math.ceil((new Date(form.due_date) - new Date(form.start_date)) / 86400000))
              : 0
          } />
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Attachments Tab — SAP B1 exact layout
   Table: #, Target Path, File Name, File Extension, File Size,
          Attachment Date, Attached By, Free Text
   Buttons: Browse, Display, Delete (stacked on the right)
   ═══════════════════════════════════════════════════════════════ */
function AttachmentsTab({ orderId, attachments, setAttachments, isLocked, isNew }) {
  const [addModal, setAddModal] = useState(false);
  const [form, setForm] = useState({ file_name: '', file_path: '', file_ext: '', attached_by: '', free_text: '' });
  const [saving, setSaving] = useState(false);

  const addAttachment = async () => {
    if (!form.file_name) return;
    setSaving(true);
    try {
      await api.post(`/production-orders/${orderId}/attachments`, {
        ...form,
        file_ext: form.file_name.includes('.') ? form.file_name.split('.').pop() : '',
      });
      const att = await api.get(`/production-orders/${orderId}/attachments`);
      setAttachments(Array.isArray(att) ? att : []);
      setAddModal(false);
    } catch (e) { alert(e.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const removeAttachment = async (attId) => {
    if (!window.confirm('Delete this attachment?')) return;
    try {
      await api.delete(`/production-orders/${orderId}/attachments/${attId}`);
      setAttachments(prev => prev.filter(a => a.attachment_id !== attId));
    } catch (e) { alert(e.message || 'Failed'); }
  };

  return (
    <div className="flex gap-4">
      {/* ── Table ── */}
      <div className="flex-1 rounded-2xl shadow-sm overflow-hidden border border-sap-border">
        <table className="w-full text-xs">
          <thead>
            <tr className="bg-gray-50 border-b border-sap-border">
              <th className={`${thCls} w-8 text-center`}>#</th>
              <th className={`${thCls} min-w-[160px]`}>Target Path</th>
              <th className={`${thCls} min-w-[140px]`}>File Name</th>
              <th className={`${thCls} w-28`}>File Extension</th>
              <th className={`${thCls} w-20 text-right`}>File Size</th>
              <th className={`${thCls} w-32`}>Attachment Date</th>
              <th className={`${thCls} w-28`}>Attached By</th>
              <th className={`${thCls} min-w-[140px]`}>Free Text</th>
            </tr>
          </thead>
          <tbody>
            {attachments.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-16 text-center text-xs text-sap-textLight italic">
                  No attachments.
                </td>
              </tr>
            ) : (
              attachments.map((a, i) => (
                <tr key={a.attachment_id}
                  className={`${i % 2 === 0 ? 'bg-white' : 'bg-[#F0F4F3]'} border-b border-sap-border/60`}>
                  <td className="px-3 py-2 text-center text-sap-textLight">{i + 1}</td>
                  <td className="px-3 py-2 text-sap-blue">{a.file_path || '—'}</td>
                  <td className="px-3 py-2 font-medium text-sap-text">{a.file_name}</td>
                  <td className="px-3 py-2 text-sap-textLight">{a.file_ext || '—'}</td>
                  <td className="px-3 py-2 text-right text-sap-textLight">{a.file_size || '—'}</td>
                  <td className="px-3 py-2 text-sap-textLight">
                    {a.attachment_date ? new Date(a.attachment_date).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2">{a.attached_by || '—'}</td>
                  <td className="px-3 py-2 text-sap-textLight">{a.free_text || '—'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Action buttons (stacked on the right, SAP B1 style) ── */}
      <div className="flex flex-col gap-2 shrink-0 pt-10">
        <button disabled={isLocked || isNew}
          onClick={() => { setForm({ file_name: '', file_path: '', file_ext: '', attached_by: '', free_text: '' }); setAddModal(true); }}
          className="w-24 py-2 text-xs font-semibold bg-sap-blue text-white rounded-lg
                     hover:bg-sap-blueDark disabled:opacity-40 shadow-sm">
          Browse
        </button>
        <button disabled
          className="w-24 py-2 text-xs font-semibold bg-gray-300 text-gray-600 rounded-lg
                     disabled:opacity-60 cursor-not-allowed shadow-sm">
          Display
        </button>
        <button disabled={isLocked || isNew || attachments.length === 0}
          className="w-24 py-2 text-xs font-semibold bg-gray-300 text-gray-600 rounded-lg
                     hover:bg-red-100 hover:text-red-600 disabled:opacity-60 disabled:cursor-not-allowed shadow-sm">
          Delete
        </button>
      </div>

      {/* Add Attachment Modal */}
      {addModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-50" onClick={() => setAddModal(false)}>
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <h3 className="text-sm font-bold text-sap-text mb-4">Add Attachment</h3>
            <div className="space-y-3">
              <div>
                <label className="text-xs text-sap-textLight mb-1 block">Target Path / URL</label>
                <input value={form.file_path} onChange={e => setForm({...form, file_path: e.target.value})}
                  placeholder="File path or URL" className={`w-full ${inp} bg-white`} />
              </div>
              <div>
                <label className="text-xs text-sap-textLight mb-1 block">File Name *</label>
                <input value={form.file_name} onChange={e => setForm({...form, file_name: e.target.value})}
                  placeholder="e.g. drawing_v2.pdf" className={`w-full ${inp} bg-white`} />
              </div>
              <div>
                <label className="text-xs text-sap-textLight mb-1 block">Attached By</label>
                <input value={form.attached_by} onChange={e => setForm({...form, attached_by: e.target.value})}
                  className={`w-full ${inp} bg-white`} />
              </div>
              <div>
                <label className="text-xs text-sap-textLight mb-1 block">Free Text</label>
                <textarea rows={2} value={form.free_text} onChange={e => setForm({...form, free_text: e.target.value})}
                  className="w-full px-3 py-2 text-xs border border-sap-border rounded-lg focus:outline-none focus:border-sap-blue bg-white" />
              </div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button onClick={() => setAddModal(false)} className="px-4 py-2 text-xs border border-sap-border rounded-lg">Cancel</button>
              <button onClick={addAttachment} disabled={saving || !form.file_name}
                className="px-4 py-2 text-xs font-semibold bg-sap-blue text-white rounded-lg disabled:opacity-40 hover:bg-sap-blueDark">
                {saving ? 'Adding…' : 'Add'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Relationship Map Modal — SAP B1 style visual diagram
   ═══════════════════════════════════════════════════════════════ */
function RelationshipMapModal({ form, issues = [], receipts = [], loading, onClose }) {
  const navigate = useNavigate();

  const fmtDateB1 = (d) => {
    if (!d) return '—';
    const dateObj = new Date(d);
    if (isNaN(dateObj.getTime())) return '—';
    const day = String(dateObj.getDate()).padStart(2, '0');
    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
    const year = String(dateObj.getFullYear()).slice(-2);
    return `${day}/${month}/${year}`;
  };

  const fmtAmt = (n) => (n != null
    ? `₹${Number(n).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
    : '—');

  /**
   * The documents either side of the order, oldest first.
   *
   * Seventeen of them used to be scattered over a fixed canvas with a separate arrow
   * drawn from the order to each, which crossed, overlapped and ran off the screen.
   * The same branching shape is kept — this is a flow diagram, not a list — but the
   * documents hang off one spine per branch instead of each pulling its own arrow
   * across the picture.
   */
  const byDate = (a, b) => new Date(a.date || 0) - new Date(b.date || 0);
  const issueDocs = (issues || []).map((i) => ({
    id: i.issue_id, number: i.issue_number, date: i.posting_date, amount: i.total_amount,
    lines: i.line_count, route: `/production/issue/${i.issue_id}`,
  })).sort(byDate);
  const receiptDocs = (receipts || []).map((r) => ({
    id: r.receipt_id, number: r.receipt_number, date: r.posting_date, amount: r.total_amount,
    route: `/production/receipt/${r.receipt_id}`,
  })).sort(byDate);

  const sum = (rows) => rows.reduce((n, r) => n + (Number(r.amount) || 0), 0);
  const hasAny = issueDocs.length > 0 || receiptDocs.length > 0;

  /** One document, drawn as a node on the diagram. */
  const Node = ({ doc, tone }) => (
    <li className="relative pl-6">
      {/* the stub joining this node to its branch spine */}
      <span className={`absolute left-0 top-1/2 h-px w-6 ${tone.line}`} aria-hidden="true" />
      <button
        type="button"
        onClick={() => navigate(doc.route)}
        className={`w-full text-left rounded-lg border bg-white px-2.5 py-1.5 shadow-sm
          hover:shadow-md hover:-translate-y-px transition-all ${tone.node}`}
      >
        <span className="block text-[11px] font-bold text-sap-text truncate">{doc.number}</span>
        <span className="flex items-baseline justify-between gap-2">
          <span className="text-[10px] text-sap-textLight">
            {fmtDateB1(doc.date)}{doc.lines ? ` · ${doc.lines} line${doc.lines === 1 ? '' : 's'}` : ''}
          </span>
          <span className="text-[10px] font-semibold text-sap-text tabular-nums">
            {fmtAmt(doc.amount)}
          </span>
        </span>
      </button>
    </li>
  );

  /** One branch of the diagram: a labelled spine with its nodes hanging off it. */
  const Branch = ({ title, docs, tone, arrow }) => (
    <div className="relative flex-1 min-w-0">
      <div className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 ${tone.chip}`}>
        <span className="text-[11px] font-bold">{arrow} {title}</span>
        <span className="text-[10px] font-semibold tabular-nums opacity-80">{docs.length}</span>
      </div>
      {docs.length > 0 && (
        <p className="mt-0.5 ml-1 text-[10px] text-sap-textLight">{fmtAmt(sum(docs))} in total</p>
      )}
      {docs.length === 0 ? (
        <p className="mt-2 ml-6 text-[11px] text-sap-textLight italic">None yet.</p>
      ) : (
        <div className="relative mt-2">
          {/* the branch spine the nodes hang from */}
          <span className={`absolute left-0 top-2 bottom-2 w-px ${tone.line}`} aria-hidden="true" />
          <ul className="space-y-1.5 max-h-[42vh] overflow-y-auto pr-1">
            {docs.map((d) => <Node key={d.id} doc={d} tone={tone} />)}
          </ul>
        </div>
      )}
    </div>
  );

  const issueTone = {
    chip: 'bg-[#FDF3E7] text-[#7A5C00] border-[#E8D5B0]',
    node: 'border-[#E8D5B0]',
    line: 'bg-[#C5A059]',
  };
  const receiptTone = {
    chip: 'bg-[#EAF3EC] text-[#2C6B41] border-[#C7E0CF]',
    node: 'border-[#C7E0CF]',
    line: 'bg-[#5B9E76]',
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border
        w-full max-w-5xl max-h-[90vh] flex flex-col overflow-hidden">

        <div className="flex items-center justify-between px-4 py-2.5 bg-[#1C3B68] text-white">
          <h3 className="text-sm font-bold">Relationship Map</h3>
          <button type="button" onClick={onClose}
            className="text-white/80 hover:text-white text-base leading-none">&#10005;</button>
        </div>

        {loading ? (
          <p className="py-16 text-center text-xs text-sap-textLight">Loading…</p>
        ) : (
          <div className="flex-1 min-h-0 overflow-auto p-5">
            <div className="flex items-stretch gap-0 min-w-[640px]">

              {/* ── the order, the root of the diagram ── */}
              <div className="flex items-center shrink-0">
                <div className="w-[210px] rounded-lg border-2 border-[#C5A059] bg-white shadow-md overflow-hidden">
                  <div className="bg-[#FEF5E7] border-b-2 border-[#C5A059] px-2.5 py-1 text-center">
                    <span className="text-[11px] font-bold text-[#7A5C00]">Production Order</span>
                  </div>
                  <div className="px-2.5 py-2 space-y-0.5 text-[11px] text-sap-text leading-tight">
                    <p className="text-sm font-bold">{form.order_no}</p>
                    <p className="font-mono text-[10px] text-sap-textLight truncate">{form.item_no}</p>
                    <p className="text-[10px] text-sap-textLight truncate">{form.item_desc || ''}</p>
                    <div className="flex items-center justify-between pt-1 text-[10px]">
                      <span className="font-semibold">{form.status}</span>
                      <span className="text-sap-textLight">
                        {form.planned_qty != null ? `${Number(form.planned_qty).toLocaleString('en-IN')} pcs` : ''}
                      </span>
                    </div>
                    <p className="text-[10px] text-sap-textLight">
                      Due {fmtDateB1(form.due_date || form.posting_date)}
                    </p>
                  </div>
                </div>
              </div>

              {/* ── the fork: one line out of the order, splitting into two branches ── */}
              <div className="w-12 shrink-0 relative" aria-hidden="true">
                <span className="absolute left-0 top-1/2 w-6 h-px bg-sap-border" />
                <span className="absolute left-6 w-px bg-sap-border"
                  style={{ top: hasAny ? '18%' : '50%', bottom: hasAny ? '18%' : '50%' }} />
                <span className="absolute left-6 right-0 h-px bg-[#C5A059]" style={{ top: '18%' }} />
                <span className="absolute left-6 right-0 h-px bg-[#5B9E76]" style={{ top: '82%' }} />
              </div>

              {/* ── the two branches ── */}
              <div className="flex-1 min-w-0 flex flex-col gap-5">
                <Branch title="Issued to production" docs={issueDocs} tone={issueTone} arrow="→" />
                <Branch title="Received from production" docs={receiptDocs} tone={receiptTone} arrow="←" />
              </div>
            </div>

            {!hasAny && (
              <p className="mt-5 text-[11px] text-sap-textLight text-center">
                Nothing has been issued or received for this order yet. Documents posted in SAP
                appear here after the next import.
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2 px-4 py-2.5 border-t bg-sap-bg/50">
          <button type="button" onClick={onClose}
            className="px-5 h-7 text-xs font-semibold bg-[#1C3B68] text-white rounded hover:bg-[#142A4B] transition-colors">
            OK
          </button>
        </div>
      </div>
    </div>
  );
}
